import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorGlobal, successGlobal } from '../reducers/notifications';
import { getAuthHeader } from '../../utils/tools';
import axios from 'axios';

axios.defaults.headers.post['Content-Type'] = 'application/json';

const buildOperationalLogPayload = (values) => ({
    branchId: values.branchId,
    facilityType: values.facilityType,
    consentId: values.consentId,
    loadingCode: values.loadingCode,
    name: values.name,
    responsiblePersons: (values.responsiblePersons ?? []).filter(p => p?.trim()),
    defaultResponsiblePersons: values.defaultResponsiblePersons ?? [],
    defaultOperatingHoursFrom: values.defaultOperatingHoursFrom || '',
    defaultOperatingHoursTo: values.defaultOperatingHoursTo || '',
});

export const fetchOperationalLogs = createAsyncThunk(
    'operationalLog/fetchOperationalLogs',
    async(params, { dispatch }) => {
        try {
            const request = await axios.get(`/api/operationallog/fetch`, {
                params: params ?? {},
                ...getAuthHeader()
            });
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw error;
        }
    }
);

export const addOperationalLog = createAsyncThunk(
    'operationalLog/addOperationalLog',
    async({ values }, { dispatch }) => {
        try {
            const payload = buildOperationalLogPayload(values);
            const request = await axios.post(`/api/operationallog/create`, payload, getAuthHeader());

            dispatch(successGlobal('Prevádzkový denník bol úspešne vytvorený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Denník sa nepodarilo vytvoriť.'));
            throw error;
        }
    }
);

export const getOperationalLogById = createAsyncThunk(
    'operationalLog/getOperationalLogById',
    async(operationalLogId, { dispatch }) => {
        try {
            const request = await axios.get(`/api/operationallog/get-by-id`, {
                params: { operationalLogId },
                ...getAuthHeader()
            });

            return request.data;
        } catch(error) {
            dispatch(errorGlobal('Denník sa nepodarilo načítať.'));
            throw error;
        }
    }
);

export const editOperationalLog = createAsyncThunk(
    'operationalLog/editOperationalLog',
    async({ operationalLogId, values }, { dispatch }) => {
        try {
            const payload = buildOperationalLogPayload(values);
            payload.operationalLogId = operationalLogId;

            const request = await axios.patch(`/api/operationallog/edit`, payload, getAuthHeader());

            dispatch(successGlobal('Prevádzkový denník bol úspešne upravený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Denník sa nepodarilo upraviť.'));
            throw error;
        }
    }
);

export const deleteOperationalLog = createAsyncThunk(
    'operationalLog/deleteOperationalLog',
    async(operationalLogId, { dispatch }) => {
        try {
            const request = await axios.delete(`/api/operationallog/delete`, {
                data: { operationalLogId },
                ...getAuthHeader()
            });

            dispatch(successGlobal('Prevádzkový denník bol úspešne vymazaný.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Denník sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const fetchEntryYears = createAsyncThunk(
    'operationalLog/fetchEntryYears',
    async({ operationalLogId }, { dispatch }) => {
        try {
            const response = await axios.get(`/api/operationallog/${operationalLogId}/entry-years`, getAuthHeader());
            return response.data;
        } catch(error) {
            dispatch(errorGlobal('Nepodarilo sa načítať roky záznamov.'));
            throw error;
        }
    }
);

export const exportOperationalLogToPdf = createAsyncThunk(
    'operationalLog/exportOperationalLogToPdf',
    async({ operationalLogId, year, month, day, quarter, name }, { dispatch }) => {
        try {
            const response = await axios.get(`/api/operationallog/${operationalLogId}/generate-pdf`, {
                params: { year, month, day, quarter },
                responseType: 'blob',
                ...getAuthHeader()
            });

            dispatch(successGlobal('Prevádzkový denník bol úspešne exportovaný do PDF.'));

            let periodLabel;
            if (day && month) periodLabel = `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
            else if (quarter) periodLabel = `Q${quarter}-${year}`;
            else if (month) periodLabel = `${String(month).padStart(2, '0')}-${year}`;
            else periodLabel = `${year}`;
            const safeName = (name || 'dennik').replace(/[^a-zA-Z0-9-_]+/g, '_');
            const filename = `Prevadzkovy-dennik-${safeName}-${periodLabel}.pdf`;

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch(error) {
            dispatch(errorGlobal('Denník sa nepodarilo exportovať do PDF.'));
            throw error;
        }
    }
);
